import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function DashboardDonut({ masteredFrames, totalFrames, overallStatus, size = 100 }) {
  const pct = totalFrames > 0 ? Math.round((masteredFrames / totalFrames) * 100) : 0;
  const r = 40;
  const cx = 55;
  const cy = 55;
  const stroke = 8;
  const startAngle = -90;
  const sweepTotal = 359.99;
  const sweepFill = (pct / 100) * sweepTotal;
  function polar(angle, radius) {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  function arcPath(startDeg, sweepDeg) {
    if (sweepDeg <= 0) return "";
    const start = polar(startDeg, r);
    const end = polar(startDeg + sweepDeg, r);
    const large = sweepDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
  }

  function getMasteryLabel(status) {
    if (status === "mastered") return "Mastered";
    if (status === "developing") return "Developing";
    if (status === "needs_focus") return "Needs Focus";
    return "Not Started";
  }

  function getMasteryColor(status) {
    if (status === "mastered") return "#22c55e";
    if (status === "developing") return "#f59e0b";
    if (status === "needs_focus") return "#ef4444";
    return "#94a3b8";
  }
  const label = getMasteryLabel(overallStatus || (pct === 0 ? "not_started" : "developing"));
  const fillClr = getMasteryColor(overallStatus);

  return (
    <svg width={size} height={size} viewBox="0 0 110 110">
      <path d={arcPath(startAngle, sweepTotal)} fill="none" stroke="#e2e8f0" strokeWidth={stroke} strokeLinecap="round" />
      {pct > 0 && (
        <path d={arcPath(startAngle, sweepFill)} fill="none" stroke={fillClr} strokeWidth={stroke} strokeLinecap="round" />
      )}
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={fillClr}>
        {label}
      </text>
    </svg>
  );
}

function MasteryDots({ masteryPct, color }) {
  // We show 5 dots. Dot is "filled" if pct >= that dot's threshold.
  const filledCount = Math.round((masteryPct / 100) * 5);
  return (
    <div className="db-dots">
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className="db-dot"
          style={{
            background: i <= filledCount ? color : "#e2e8f0"
          }}
        />
      ))}
    </div>
  );
}

function formatChapterId(id) {
  if (!id) return "Unknown";
  return id
    .replace(/-/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function Dashboard({ user, username, apiBase, token, onPageBusyChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [assignedSets, setAssignedSets] = useState([]);
  const navigate = useNavigate();

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("dashboard", true, "Loading dashboard...");
    }
    try {
      const [resDashboard, resSessions, resAssigned] = await Promise.all([
        fetch(`${apiBase}/api/mastery/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/student/sessions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/api/questions/assigned-to-me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (!resDashboard.ok) throw new Error("Failed to load dashboard");
      const json = await resDashboard.json();
      setData(json);

      if (resSessions.ok) {
        const sessData = await resSessions.json();
        setSessions(sessData.sessions || []);
      }

      if (resAssigned && resAssigned.ok) {
        const assData = await resAssigned.json();
        setAssignedSets(assData.sets || []);
      }
    } catch (err) {
      console.error("[Dashboard] fetch error:", err);
    } finally {
      setLoading(false);
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("dashboard", false);
      }
    }
  }, [apiBase, token, onPageBusyChange]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const upcomingSession = useMemo(() => {
    const now = Date.now() - 30 * 60 * 1000;
    const future = sessions
      .filter((s) => s.status !== "cancelled" && new Date(s.startTime).getTime() >= now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    return future[0] || null;
  }, [sessions]);

  const pendingAssignments = useMemo(() => {
    return assignedSets.filter((s) => {
      const isTest = s.assignmentType === "test" || s.sourceType === "exam_paper";
      if (isTest) return !s.isLocked && !s.isGraded;
      return s.completedCount < s.questionCount;
    });
  }, [assignedSets]);

  const overall = data?.overall || {};
  const chapters = data?.chapters || [];
  const totalFrames = overall.totalFrames || 0;
  const masteredFrames = overall.mastered || 0;
  const developingFrames = overall.developing || 0;
  const currentStreak = overall.currentStreak || 0;
  const longestStreak = overall.longestStreak || 0;
  const recommendedNext = data?.recommendedNext || null;
  const masterPct = totalFrames > 0 ? Math.round((masteredFrames / totalFrames) * 100) : 0;

  const strongConcepts = [...chapters]
    .filter(c => c.mastered > 0)
    .sort((a, b) => b.masteryPct - a.masteryPct)
    .slice(0, 3);

  const needsPracticeConcepts = [...chapters]
    // Only include chapters where they ACTUALLY have frames that need practice (developing or needs_focus)
    .filter(c => c.totalFrames > 0 && c.masteryPct < 100 && (c.developing > 0 || c.needsFocus > 0))
    .sort((a, b) => {
      if (a.masteryPct !== b.masteryPct) {
        return a.masteryPct - b.masteryPct;
      }

      if (a.needsFocus !== b.needsFocus) {
        return b.needsFocus - a.needsFocus;
      }

      if (a.developing !== b.developing) {
        return b.developing - a.developing;
      }

      return b.totalFrames - a.totalFrames;
    })
    .slice(0, 3);

  const DIFF_COLOR = { LOT: "#22c55e", MOT: "#f59e0b", HOT: "#ef4444" };

  const displayName = username || user?.displayName || user?.email?.split("@")[0] || "Student";

  if (loading) {
    return (
      <section className="page">
        <div className="page-sub">Loading dashboard...</div>
      </section>
    );
  }

  return (
    <section className="page db-page">

      {/* ── Top Row: Greeting + Streak ── */}
      <div className="db-top-row">
        <div className="db-greeting">
          <h1 className="db-greeting-title">
            {getGreeting()}, {displayName}! 👋
          </h1>
          <p className="db-greeting-sub">Let's continue your math journey.</p>
        </div>
        <div className="db-streak-badge">
          <span className="db-streak-icon">🔥</span>
          <div>
            <div className="db-streak-number">{currentStreak} Day Streak</div>
            <div className="db-streak-sub">
              {currentStreak === 0 ? "Start practising today!" : "Keep it going!"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick Action: Solve Your Own Question ── */}
      <div
        className="db-custom-question-banner"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
          border: "1px solid #bfdbfe",
          borderRadius: "14px",
          padding: "14px 20px",
          marginTop: "16px",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              fontSize: "24px",
              background: "#dbeafe",
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            ✍️
          </div>
          <div>
            <div style={{ fontWeight: "700", fontSize: "15px", color: "#1e3a8a" }}>
              Have a math problem to solve?
            </div>
            <div style={{ fontSize: "13px", color: "#475569" }}>
              Upload an image, take a photo, or handwrite with your stylus to practice with live AI guidance.
            </div>
          </div>
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => navigate("/my-questions")}
          style={{ whiteSpace: "nowrap", padding: "8px 18px", fontSize: "14px" }}
        >
          Upload & Solve →
        </button>
      </div>

      {/* ── Stats Row: 4 Cards ── */}
      <div className="db-stats-row">
        {/* Card 1: Overall Mastery (with donut) */}
        <div className="db-stat-card db-stat-card--wide">
          <div className="db-stat-label">Overall Mastery</div>
          <div className="db-stat-wide-inner">
            <div className="db-stat-wide-left">
              <div className="db-stat-big">{masterPct}%</div>
            </div>
            <div className="db-stat-donut">
              <DashboardDonut
                masteredFrames={masteredFrames}
                totalFrames={totalFrames}
                overallStatus={overall.overallStatus}
                size={110}
              />
            </div>
          </div>
        </div>

        {/* Card 2: Frames Mastered */}
        <div className="db-stat-card">
          <div className="db-stat-label">Frames Mastered</div>
          <div className="db-stat-big">{masteredFrames} <span className="db-stat-total">/ {totalFrames}</span></div>
          <div className="db-stat-sub">Total Frames</div>
        </div>

        {/* Card 3: Developing Frames (replaces Accuracy) */}
        <div className="db-stat-card">
          <div className="db-stat-label">Developing</div>
          <div className="db-stat-big">{developingFrames} <span className="db-stat-total">/ {totalFrames}</span></div>
          <div className="db-stat-sub">Total Frames</div>
        </div>

        {/* Card 4: Current Streak */}
        <div className="db-stat-card">
          <div className="db-stat-label">Current Streak</div>
          <div className="db-stat-big db-stat-streak">
            {currentStreak} Days <span className="db-streak-flame">🔥</span>
          </div>
          <div className="db-stat-sub">Best: {longestStreak} days</div>
        </div>
      </div>

      {/* ── Bottom Row: 3 Concept Cards ── */}
      <div className="db-bottom-row">
        {/* Card 1: Concepts Strong */}
        <div className="db-concept-card">
          <div className="db-concept-header">
            <span className="db-concept-icon db-concept-icon--green">✅</span>
            <span className="db-concept-title">Concepts Strong</span>
          </div>
          {strongConcepts.length === 0 ? (
            <div className="db-concept-empty">Keep practising to see your strengths here!</div>
          ) : (
            <div className="db-concept-list">
              {strongConcepts.map(ch => (
                <div key={ch.chapterId} className="db-concept-row">
                  <span className="db-concept-name">{formatChapterId(ch.chapterId)}</span>
                  <MasteryDots masteryPct={ch.masteryPct} color="#22c55e" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card 2: Needs More Practice */}
        <div className="db-concept-card">
          <div className="db-concept-header">
            <span className="db-concept-icon db-concept-icon--orange">⚠️</span>
            <span className="db-concept-title">Needs More Practice</span>
          </div>
          {needsPracticeConcepts.length === 0 ? (
            <div className="db-concept-empty">Great job! No weak areas found.</div>
          ) : (
            <div className="db-concept-list">
              {needsPracticeConcepts.map(ch => (
                <div key={ch.chapterId} className="db-concept-row">
                  <span className="db-concept-name">{formatChapterId(ch.chapterId)}</span>
                  <MasteryDots masteryPct={ch.masteryPct} color="#f59e0b" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card 3: Recommended Next */}
        <div className="db-concept-card db-concept-card--rec">
          <div className="db-concept-header">
            <span className="db-concept-icon db-concept-icon--purple">🎯</span>
            <span className="db-concept-title">Recommended Next</span>
          </div>
          {recommendedNext ? (
            <>
              <div className="db-rec-frame-title">
                {recommendedNext.frameTitle}
                <span
                  className="db-rec-diff-badge"
                  style={{ background: DIFF_COLOR[recommendedNext.difficulty] || "#94a3b8" }}
                >
                  {recommendedNext.difficulty}
                </span>
              </div>
              <div className="db-rec-chapter">
                in {formatChapterId(recommendedNext.chapterId)}
              </div>
              <button
                className="db-rec-btn"
                onClick={() => {
                  const params = new URLSearchParams({
                    difficulty: recommendedNext.difficulty,
                    frameOrder: String(recommendedNext.frameOrder ?? "")
                  });
                  navigate(`/practice/${recommendedNext.chapterId}?${params.toString()}`);
                }}
              >
                Start Now →
              </button>
            </>
          ) : (
            <>
              <div className="db-rec-frame-title">
                {(overall.totalAttempts || 0) === 0
                  ? "Start your journey! 🚀"
                  : "All caught up! 🎉"}
              </div>
              <div className="db-rec-chapter">
                {(overall.totalAttempts || 0) === 0
                  ? "You haven't practised yet. Let's begin!"
                  : "You have mastered all available frames."}
              </div>
            </>
          )}
        </div>

      </div>

      {/* ── Tutor Action Reminders: Next Class & Pending Coursework ── */}
      {(upcomingSession || pendingAssignments.length > 0 || sessions.length > 0 || assignedSets.length > 0) && (
        <div style={{ marginTop: "32px", marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
            <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
              Tutor Reminders & Schedule
            </h2>
            <button
              type="button"
              onClick={() => navigate("/my-tutor")}
              style={{
                background: "none",
                border: "none",
                color: "#2563eb",
                fontWeight: "600",
                fontSize: "13px",
                cursor: "pointer",
                padding: "2px 6px"
              }}
            >
              Open My Tutor Hub →
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "16px"
            }}
          >
            {/* Card 1: Next Live Class */}
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid var(--border)",
                borderRadius: "14px",
                padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#475569", letterSpacing: "0.5px" }}>
                    Next Live Class
                  </span>
                  {upcomingSession && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        backgroundColor: upcomingSession.sessionType === "one_on_one" ? "#fdf2f8" : "#eff6ff",
                        color: upcomingSession.sessionType === "one_on_one" ? "#be185d" : "#1d4ed8"
                      }}
                    >
                      {upcomingSession.sessionType === "one_on_one" ? "1:1 Session" : "Group Class"}
                    </span>
                  )}
                </div>

                {upcomingSession ? (
                  <>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: "0 0 6px 0" }}>
                      {upcomingSession.title}
                    </h3>
                    <div style={{ fontSize: "13px", color: "#334155", marginBottom: "4px" }}>
                      {new Date(upcomingSession.startTime).toLocaleDateString("en-IN", {
                        weekday: "short",
                        month: "short",
                        day: "numeric"
                      })}{" "}
                      at{" "}
                      {new Date(upcomingSession.startTime).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit"
                      })}{" "}
                      ({upcomingSession.durationMinutes || 60} mins)
                    </div>
                    {upcomingSession.classroomName && (
                      <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                        Batch: {upcomingSession.classroomName}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: "var(--muted)", fontSize: "13px", padding: "12px 0" }}>
                    No upcoming live classes scheduled for today.
                  </div>
                )}
              </div>

              <div style={{ marginTop: "16px", borderTop: "1px solid #f1f5f9", paddingTop: "12px", display: "flex", justifyContent: "flex-end" }}>
                {upcomingSession?.meetingUrl ? (
                  <a
                    href={upcomingSession.meetingUrl.startsWith("http") ? upcomingSession.meetingUrl : `https://${upcomingSession.meetingUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="primary-button"
                    style={{ textDecoration: "none", fontSize: "12px", padding: "6px 14px", backgroundColor: "#059669" }}
                  >
                    Join Online Class →
                  </a>
                ) : (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => navigate("/my-tutor")}
                    style={{ fontSize: "12px", padding: "6px 14px" }}
                  >
                    View Schedule in My Tutor →
                  </button>
                )}
              </div>
            </div>

            {/* Card 2: Tests & Homework Due */}
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid var(--border)",
                borderRadius: "14px",
                padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#475569", letterSpacing: "0.5px" }}>
                    Coursework Due
                  </span>
                  {pendingAssignments.length > 0 && (
                    <span style={{ fontSize: "11px", fontWeight: "700", backgroundColor: "#fee2e2", color: "#b91c1c", padding: "2px 8px", borderRadius: "4px" }}>
                      {pendingAssignments.length} Pending
                    </span>
                  )}
                </div>

                {pendingAssignments.length > 0 ? (
                  <>
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: "0 0 4px 0" }}>
                      {pendingAssignments[0].title}
                    </h3>
                    <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "4px" }}>
                      {pendingAssignments[0].assignmentType === "test" || pendingAssignments[0].sourceType === "exam_paper"
                        ? "Timed Test"
                        : "Homework"}{" "}
                      • {pendingAssignments[0].questionCount} Problems
                    </div>
                    {(pendingAssignments[0].dueTimestamp || pendingAssignments[0].dueDate) && (
                      <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "600" }}>
                        Due: {new Date(pendingAssignments[0].dueTimestamp || pendingAssignments[0].dueDate).toLocaleDateString("en-IN", {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: "#059669", fontSize: "13px", padding: "12px 0", fontWeight: "500" }}>
                    ✓ All assigned tests and homework are up to date!
                  </div>
                )}
              </div>

              <div style={{ marginTop: "16px", borderTop: "1px solid #f1f5f9", paddingTop: "12px", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => navigate("/my-tutor")}
                  style={{ fontSize: "12px", padding: "6px 14px", backgroundColor: "#2563eb" }}
                >
                  {pendingAssignments.length > 0 ? "Solve on My Tutor →" : "View Coursework Hub →"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
